import { useSuggestedArticle } from 'app/context/SuggestedArticleProvider';
import { SuggestedEntry } from 'app/copied/models/ChatModules';
import useRxConditionId from 'app/hooks/useRxConditionId';
import useRxId from 'app/hooks/useRxId';
import { ContactFormLinkLocation } from 'app/modules/contact-form/stores/ContactFormStore';
import { getConfig } from 'app/services/config';
import { useAimSignalStateStore } from 'app/stores/AimSignalStateStore';
import { isContactFormURL } from 'app/utils/contactFormUrl';
import { useNavigate } from 'react-router-dom';
import { logError } from 'app/services/logger';

import useSelectedIsSuggested from '../../../hooks/analytics/useSelectedIsSuggested';
import useArticles from './useArticles';
import useLinks, { isContactURL } from './useLinks';
import usePdfViewerEnabled from './usePdfViewerEnabled';
import { useSearch } from './useSearch';
import useTailorTracking from './useTailorTracking';

export default function useHandleSearchResultClick() {
  const [conditionId] = useRxConditionId();
  const [rxId] = useRxId();
  const { setLastViewedArticle } = useSuggestedArticle();
  const { captureSearchResultClick } = useTailorTracking();
  const navigate = useNavigate();
  const [, setSelectedIsSuggested] = useSelectedIsSuggested();
  const { search } = useSearch();
  const { openLink, checkIsPdf } = useLinks();
  const { addArticle } = useArticles();
  const pdfViewerEnabled = usePdfViewerEnabled();
  const { setHasExternalLinkBeenClicked } = useAimSignalStateStore();

  const createPdfViewerUrl = (pdfUrl: string, searchText: string, result: SuggestedEntry) => {
    const appUrl = getConfig('APP_URL');
    const pdfViewerUrl = new URL(`${appUrl}/pdf.html`);

    pdfViewerUrl.searchParams.set('url', pdfUrl);
    if (searchText) {
      pdfViewerUrl.searchParams.set('search', searchText);
    }

    const widgetData = {
      conditionId,
      rxId,
      lastViewedArticle: result,
    };

    try {
      const widgetDataJson = JSON.stringify(widgetData);
      const widgetDataBase64 = btoa(widgetDataJson);
      pdfViewerUrl.searchParams.set('data', widgetDataBase64);
    } catch (error) {
      logError('Failed to encode widget data:', error);
      pdfViewerUrl.searchParams.set('conditionId', conditionId);
      pdfViewerUrl.searchParams.set('rxId', rxId);
      pdfViewerUrl.searchParams.set('lastViewedArticle', JSON.stringify(result));
    }

    return pdfViewerUrl.toString();
  };

  const handleResultSelect = ({
    result,
    resultIndex,
    isSuggested,
    categoryTitle,
    linkDetails,
    skipAnalytics = false,
    linkLocation,
  }: {
    result: SuggestedEntry;
    resultIndex: number;
    isSuggested: boolean;
    categoryTitle?: string;
    linkDetails?: { source?: 'widget' | 'nonWidget'; type?: 'resource' | 'external'; action_type?: string };
    skipAnalytics?: boolean;
    linkLocation?: ContactFormLinkLocation;
  }) => {
    addArticle(result);
    setLastViewedArticle(result);
    setSelectedIsSuggested(isSuggested);
    const url = result.external_link || result.resource_link;
    if (!skipAnalytics) {
      captureSearchResultClick(result, {
        resultIndex,
        search,
        categoryTitle,
      });
    }
    if (url) {
      if (result.external_link && !isContactFormURL(result.external_link) && !isContactURL(result.external_link)) {
        setHasExternalLinkBeenClicked(true);
      }

      if (checkIsPdf(url) && pdfViewerEnabled) {
        const pdfViewerUrl = createPdfViewerUrl(url, search, result);
        window.open(pdfViewerUrl, '_blank');
      } else {
        openLink(url, {
          type: result.resource_link ? 'resource' : 'external',
          source: linkDetails?.source,
          linkLocation,
        });
      }
      return;
    }
    if (result.node_ref_id) {
      navigate(`/virtual-assistant/pill/${result.node_ref_id}/${result.title}`, {
        state: { skipAnimation: true, source: linkDetails?.source },
      });
      return;
    }
    navigate(`/tailor/article/${result.id}`);
  };

  return handleResultSelect;
}
